import { useMemo, useCallback, useEffect } from 'react';
import * as THREE from 'three';
import { ThreeEvent } from '@react-three/fiber';
import { useSketchStore, type SketchSelectionId } from '../../state/useSketchStore';
import { useAppStore } from '../../state/store';
import { faceSelectionId, solidEffectiveVisible } from '../../core/sketch/types';
import { faceToGeometry } from '../../core/sketch/triangulate';
import { buildTopology } from '../../core/sketch/topology';
import { faceMaterialProps, applyBackfaceTint } from './faceMaterial';
import { SKETCH_COLORS } from '../../themes/colors';
import { emitSketchDown, emitSketchMove } from '../../tools/sketch/sketchToolBus';

/**
 * Sketch solid layer — one interactive mesh per FACE.
 *
 * Face-per-mesh is the Sketch contract: hover/selection highlight a face,
 * Paint recolors a face, Push/Pull grabs a face. Geometry is derived at
 * render time from the analytic loops (core/sketch/triangulate) and cached
 * per (solid, face) until the loops change. Edge lines are derived from the
 * face contours directly (not EdgesGeometry) so curved borders keep their
 * full tessellation.
 */

/** Stable cache key: same loops → same geometry. */
const faceSignature = (points: Array<[number, number, number]>): string =>
    points.map((p) => `${p[0].toFixed(5)},${p[1].toFixed(5)},${p[2].toFixed(5)}`).join(';');

function FaceMesh({ solidId, faceId, style, shadowsEnabled }: {
    solidId: string;
    faceId: string;
    style: ReturnType<typeof faceMaterialProps>;
    shadowsEnabled: boolean;
}) {
    const solid = useSketchStore((s) => s.solids[solidId]);
    const face = solid?.faces.find((f) => f.id === faceId);
    const selectedIds = useSketchStore((s) => s.selectedIds);
    const hoveredId = useSketchStore((s) => s.hoveredId);
    const setHovered = useSketchStore((s) => s.setHovered);
    const faceHoverHighlight = useAppStore((s) => s.sketchOptions.faceHoverHighlight);
    const faceHoverColor = useAppStore((s) => s.sketchOptions.faceHoverColor);

    const selectionId: SketchSelectionId = faceSelectionId(solidId, faceId);
    const isSelected = selectedIds.includes(selectionId);
    const isHovered = faceHoverHighlight && hoveredId === selectionId;

    const geometry = useMemo(() => (face ? faceToGeometry(face) : null), [face]);

    const color = isSelected
        ? SKETCH_COLORS.faceSelected
        : isHovered
            ? faceHoverColor
            : style.color;

    const handleDown = useCallback((e: ThreeEvent<PointerEvent>) => {
        if (e.button !== 0) return;
        // Face hits stop here: the event plane must not see a face pick as
        // "empty space". The bus routes the hit to the active tool, or to
        // the default face-select contract when no tool owns the pointer.
        e.stopPropagation();
        emitSketchDown({
            kind: 'face',
            solidId,
            faceId,
            point: e.point.clone(),
            shiftKey: e.nativeEvent.shiftKey,
            ctrlKey: e.nativeEvent.ctrlKey,
            altKey: e.nativeEvent.altKey,
            button: e.button,
            pointerPx: { x: e.nativeEvent.clientX, y: e.nativeEvent.clientY },
        });
    }, [solidId, faceId]);

    const handleMove = useCallback((e: ThreeEvent<PointerEvent>) => {
        e.stopPropagation();
        emitSketchMove({
            kind: 'face',
            solidId,
            faceId,
            point: e.point.clone(),
            shiftKey: e.nativeEvent.shiftKey,
            ctrlKey: e.nativeEvent.ctrlKey,
            altKey: e.nativeEvent.altKey,
            button: -1,
            pointerPx: { x: e.nativeEvent.clientX, y: e.nativeEvent.clientY },
        });
    }, [solidId, faceId]);

    const handleOver = useCallback((e: ThreeEvent<PointerEvent>) => {
        e.stopPropagation();
        setHovered(selectionId);
    }, [setHovered, selectionId]);

    // One material instance per face mesh, built imperatively so the
    // backface tint patch applies BEFORE the first compile (a ref-based
    // patch would need a forced recompile on mount). Hooks stay above the
    // early return: a face toggling between null/defined must not change
    // the hook order.
    const material = useMemo(() => {
        const m = style.flat
            ? new THREE.MeshBasicMaterial()
            : new THREE.MeshStandardMaterial({ roughness: style.roughness, metalness: style.metalness });
        if (style.backfaceTint) applyBackfaceTint(m);
        return m;
    }, [style.flat, style.roughness, style.metalness, style.backfaceTint]);

    useEffect(() => () => material.dispose(), [material]);

    if (!face || !geometry) return null;

    const surfaceProps = {
        color,
        transparent: style.transparent || isSelected || isHovered,
        opacity: isSelected ? 1 : isHovered ? 0.85 : style.opacity,
        depthWrite: style.depthWrite,
        side: THREE.DoubleSide,
    };
    material.color.set(color);
    material.opacity = surfaceProps.opacity;
    material.transparent = surfaceProps.transparent;
    material.depthWrite = surfaceProps.depthWrite;

    return (
        <mesh
            geometry={geometry}
            castShadow={shadowsEnabled}
            receiveShadow={shadowsEnabled}
            onPointerDown={handleDown}
            onPointerMove={handleMove}
            onPointerOver={handleOver}
            onPointerOut={() => { if (useSketchStore.getState().hoveredId === selectionId) setHovered(null); }}
        >
            <primitive object={material} attach="material" />
        </mesh>
    );
}

function SolidEdges({ solidId, edgeColor, edgeOpacity }: { solidId: string; edgeColor: string; edgeOpacity: number }) {
    const solid = useSketchStore((s) => s.solids[solidId]);

    const segments = useMemo(() => {
        if (!solid) return [];
        const topo = buildTopology(solid);
        const pts: number[] = [];
        topo.edges.forEach((edge) => {
            pts.push(edge.a.x, edge.a.y, edge.a.z, edge.b.x, edge.b.y, edge.b.z);
        });
        return pts;
    }, [solid]);

    if (segments.length === 0) return null;
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(segments), 3));

    return (
        <lineSegments geometry={geometry} raycast={() => null}>
            <lineBasicMaterial color={edgeColor} transparent={edgeOpacity < 1} opacity={edgeOpacity} />
        </lineSegments>
    );
}

export function SketchSolidLayer() {
    const solids = useSketchStore((s) => s.solids);
    const tags = useSketchStore((s) => s.tags);
    const displayStyle = useSketchStore((s) => s.displayStyle);
    const shadowsEnabled = useSketchStore((s) => s.shadows.enabled);

    return (
        <group>
            {Object.values(solids).map((solid) => {
                // Visibility = the solid's own flag AND its tag's flag.
                if (!solidEffectiveVisible(solid, tags)) return null;
                const style = faceMaterialProps(displayStyle, undefined, solid.materialId);
                return (
                    <group key={solid.id}>
                    {solid.faces.map((face) => (
                        <FaceMesh
                            key={face.id + faceSignature(face.outer)}
                            solidId={solid.id}
                            faceId={face.id}
                            style={face.materialId
                                ? faceMaterialProps(displayStyle, face.materialId, solid.materialId)
                                : style}
                            shadowsEnabled={shadowsEnabled}
                        />
                    ))}
                    {style.edgeOpacity > 0 && (
                        <SolidEdges solidId={solid.id} edgeColor={style.edgeColor} edgeOpacity={style.edgeOpacity} />
                    )}
                    </group>
                );
            })}
        </group>
    );
}
